import Checkbox, { CheckboxLabel } from 'components/Checkbox'
import Select from 'components/Select'
import styled from 'styled-components'
import type { RdnPricingCapabilities, RdnPricingMethod } from 'types/match'

const NEW_ANNUAL_OBSERVATIONS = [1, 30, 365, 1095]

type Props = {
  method: RdnPricingMethod
  pricingCutoffDate?: string
  capabilities: RdnPricingCapabilities | null
  capabilitiesError: boolean
  onChange: (method: RdnPricingMethod) => void
  onRetry: () => void
  disabled?: boolean
}

export default function RdnPricingMethodEditor({
  method,
  pricingCutoffDate,
  capabilities,
  capabilitiesError,
  onChange,
  onRetry,
  disabled
}: Props) {
  const methodCapability = (candidate: RdnPricingMethod) =>
    capabilities?.methods.find(capability => sameMethod(capability.method, candidate))
  const isUnavailable = (candidate: RdnPricingMethod) =>
    methodCapability(candidate)?.available === false
  const optionLabel = (label: string, candidate: RdnPricingMethod) => {
    const capability = methodCapability(candidate)
    return capability?.available === false && capability.unavailable_reason
      ? `${label} — ${capability.unavailable_reason}`
      : label
  }
  const firstAvailableMethod = (kind: RdnPricingMethod['kind']) =>
    capabilities?.methods.find(
      capability =>
        capability.method.kind === kind &&
        capability.available &&
        isSelectableForNewConfiguration(capability.method)
    )?.method
  const movingAverageHistoryChoices = (windowDays: number) =>
    windowDays === 1 ? [2, 3] : [1, 2, 3]
  const isKindUnavailable = (kind: RdnPricingMethod['kind']) =>
    capabilities !== null &&
    !capabilities.methods.some(
      capability =>
        capability.method.kind === kind &&
        capability.available &&
        isSelectableForNewConfiguration(capability.method)
    )
  const isMovingAverageWindowUnavailable = (windowDays: number) =>
    capabilities !== null &&
    movingAverageHistoryChoices(windowDays).every(historyYears =>
      isUnavailable({
        kind: 'CALENDAR_MOVING_AVERAGE',
        window_days: windowDays,
        history_years: historyYears
      })
    )

  const annual = method.kind === 'ANNUAL_HOURLY_PROFILE' ? method : null
  const movingAverage = method.kind === 'CALENDAR_MOVING_AVERAGE' ? method : null
  const selectedCapability = methodCapability(method)

  return (
    <Controls>
      <Select
        aria-label="Metoda wyznaczania ceny energii RDN"
        value={method.kind}
        onChange={event => {
          const kind = event.currentTarget.value as RdnPricingMethod['kind']
          if (kind === 'ANNUAL_HOURLY_PROFILE') {
            const preferred: RdnPricingMethod = {
              kind,
              observations: 30,
              skip_weekends: false
            }
            onChange(
              !isUnavailable(preferred) ? preferred : firstAvailableMethod(kind) ?? preferred
            )
          } else if (kind === 'CALENDAR_MOVING_AVERAGE') {
            const preferred: RdnPricingMethod = { kind, window_days: 1, history_years: 3 }
            onChange(
              !isUnavailable(preferred) ? preferred : firstAvailableMethod(kind) ?? preferred
            )
          } else {
            const year = capabilities?.complete_historical_years[0]
            if (year) onChange({ kind: 'HISTORICAL_YEAR', year })
          }
        }}
        disabled={disabled}
      >
        <option value="ANNUAL_HOURLY_PROFILE" disabled={isKindUnavailable('ANNUAL_HOURLY_PROFILE')}>
          Dobowy profil godzinowy
        </option>
        <option
          value="CALENDAR_MOVING_AVERAGE"
          disabled={isKindUnavailable('CALENDAR_MOVING_AVERAGE')}
        >
          Średnia krocząca kalendarzowa
        </option>
        <option value="HISTORICAL_YEAR" disabled={!capabilities?.complete_historical_years.length}>
          Dostępny rok historyczny
        </option>
      </Select>

      {annual && (
        <>
          <Select
            aria-label="Liczba obserwacji"
            value={annual.observations}
            onChange={event =>
              onChange({ ...annual, observations: Number(event.currentTarget.value) })
            }
            disabled={disabled}
          >
            {[
              ...NEW_ANNUAL_OBSERVATIONS,
              ...([7, 90, 180, 730].includes(annual.observations) ? [annual.observations] : [])
            ]
              .filter((item, index, all) => all.indexOf(item) === index)
              .map(observations => {
                const candidate: RdnPricingMethod = {
                  kind: 'ANNUAL_HOURLY_PROFILE',
                  observations,
                  skip_weekends: annual.skip_weekends
                }
                return (
                  <option
                    key={observations}
                    value={observations}
                    disabled={isUnavailable(candidate)}
                  >
                    {optionLabel(`${observations} dni`, candidate)}
                  </option>
                )
              })}
          </Select>
          <StyledCheckboxLabel>
            <Checkbox
              checked={annual.skip_weekends}
              onChange={() => onChange({ ...annual, skip_weekends: !annual.skip_weekends })}
              disabled={disabled}
            />
            Pomiń weekendy
          </StyledCheckboxLabel>
        </>
      )}

      {movingAverage && (
        <>
          <Select
            aria-label="Okno średniej kroczącej"
            value={movingAverage.window_days}
            onChange={event => {
              const windowDays = Number(event.currentTarget.value)
              const histories = movingAverageHistoryChoices(windowDays)
              const currentHistory = movingAverage.history_years
              const historyYears =
                histories.includes(currentHistory) &&
                !isUnavailable({
                  kind: 'CALENDAR_MOVING_AVERAGE',
                  window_days: windowDays,
                  history_years: currentHistory
                })
                  ? currentHistory
                  : histories.find(
                      candidate =>
                        !isUnavailable({
                          kind: 'CALENDAR_MOVING_AVERAGE',
                          window_days: windowDays,
                          history_years: candidate
                        })
                    ) ?? histories[0]
              onChange({
                kind: 'CALENDAR_MOVING_AVERAGE',
                window_days: windowDays,
                history_years: historyYears
              })
            }}
            disabled={disabled}
          >
            {[1, 3, 7, 15, 29].map(windowDays => (
              <option
                key={windowDays}
                value={windowDays}
                disabled={isMovingAverageWindowUnavailable(windowDays)}
              >
                Okno {windowDays} dni
              </option>
            ))}
          </Select>
          <Select
            aria-label="Liczba lat historii"
            value={movingAverage.history_years}
            onChange={event =>
              onChange({
                ...movingAverage,
                history_years: Number(event.currentTarget.value)
              })
            }
            disabled={disabled}
          >
            {movingAverageHistoryChoices(movingAverage.window_days).map(historyYears => {
              const candidate: RdnPricingMethod = { ...movingAverage, history_years: historyYears }
              return (
                <option key={historyYears} value={historyYears} disabled={isUnavailable(candidate)}>
                  {optionLabel(historyYears === 1 ? '1 rok' : `${historyYears} lata`, candidate)}
                </option>
              )
            })}
          </Select>
        </>
      )}

      {method.kind === 'HISTORICAL_YEAR' && (
        <Select
          aria-label="Rok historyczny"
          value={method.year}
          onChange={event =>
            onChange({
              kind: 'HISTORICAL_YEAR',
              year: Number(event.currentTarget.value)
            })
          }
          disabled={disabled}
        >
          {capabilities?.complete_historical_years.map(year => (
            <option key={year} value={year}>
              {year}
            </option>
          ))}
        </Select>
      )}

      {capabilitiesError && (
        <UnavailableReason>
          Nie udało się sprawdzić dostępności metod RDN.{' '}
          <button type="button" onClick={onRetry}>
            Spróbuj ponownie
          </button>
        </UnavailableReason>
      )}
      {pricingCutoffDate && method.kind !== 'HISTORICAL_YEAR' && (
        <PricingCutoff>Data graniczna cen RDN: {pricingCutoffDate}</PricingCutoff>
      )}
      {!capabilitiesError && capabilities?.historical_unavailable_reason && (
        <UnavailableReason>{capabilities.historical_unavailable_reason}</UnavailableReason>
      )}
      {selectedCapability?.available === false && (
        <UnavailableReason>{selectedCapability.unavailable_reason}</UnavailableReason>
      )}
    </Controls>
  )
}

export function sameMethod(left: RdnPricingMethod, right: RdnPricingMethod): boolean {
  if (left.kind !== right.kind) return false
  if (left.kind === 'ANNUAL_HOURLY_PROFILE' && right.kind === 'ANNUAL_HOURLY_PROFILE') {
    return left.observations === right.observations && left.skip_weekends === right.skip_weekends
  }
  if (left.kind === 'CALENDAR_MOVING_AVERAGE' && right.kind === 'CALENDAR_MOVING_AVERAGE') {
    return left.window_days === right.window_days && left.history_years === right.history_years
  }
  return (
    left.kind === 'HISTORICAL_YEAR' && right.kind === 'HISTORICAL_YEAR' && left.year === right.year
  )
}

function isSelectableForNewConfiguration(method: RdnPricingMethod): boolean {
  return (
    method.kind !== 'ANNUAL_HOURLY_PROFILE' ||
    NEW_ANNUAL_OBSERVATIONS.includes(method.observations)
  )
}

const Controls = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;

  > select {
    width: 100%;
  }
`

const StyledCheckboxLabel = styled(CheckboxLabel)`
  align-self: flex-start;
`

const PricingCutoff = styled.small`
  color: var(--dark-gray);
`

const UnavailableReason = styled.small`
  color: var(--red);
`
